/* Conversion-optimization layer for the Sell-My-Device Instant Estimate widget.
   Each export is a small drop-in component that mounts at a specific spot in
   InstantEstimate.jsx, they all hit /api/conversion/* endpoints.

   Components exported:
     <SocialProofTicker />, Live "Maria from Chicago just sold..." pill
     <TrustStrip />, BBB / reviews / 10k+ devices badge row
     <PriceLockBadge price />, "Your $X offer is locked for 7 days"
     <OfferCountdown />, Live 23:59:59 countdown
     <BeatAnyQuoteBadge price />, "Show us higher → we beat by $20"
     <PriceTrendBanner device />, Seasonal urgency banner
     <SmsOfferButton ...props />, "Text me this offer" CTA
     <ExitIntentModal ...props />, Capture email when user moves to leave
     <PhotoConditionPicker ... />, One-tap AI condition grading
     <TradeUpsell price />, Refurb store-credit upsell
     <ReferralKickback />, Apply 2A-XXXX referral code
*/
import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Lock, Shield, Star, TrendingUp, Camera, MessageSquare, Gift, X,
  Clock, ArrowUpRight, Sparkles, Award, CheckCircle2, Mail } from 'lucide-react';

const API_URL = (() => {
  const envUrl = process.env.REACT_APP_BACKEND_URL;
  if (!envUrl) return window.location.origin;
  try {
    const envHost = new URL(envUrl).hostname;
    const currentHost = window.location.hostname;
    if (currentHost === 'localhost' || envHost === currentHost ||
        currentHost.includes('preview.emergentagent.com')) return envUrl;
    return window.location.origin;
  } catch { return window.location.origin; }
})();

const post = (path, body) =>
  fetch(`${API_URL}${path}`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  }).then(r => r.ok ? r.json() : Promise.reject(r));

const get = (path) =>
  fetch(`${API_URL}${path}`, { cache: 'no-store' }).then(r => r.ok ? r.json() : Promise.reject(r));


/* ─── 1) Social-Proof Ticker (P0 trust) ────────────────────────────────── */
export function SocialProofTicker() {
  const [sales, setSales] = useState([]);
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    let alive = true;
    get('/api/conversion/recent-sales?limit=10')
      .then(d => alive && setSales(d?.sales || []))
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  useEffect(() => {
    if (sales.length < 2) return;
    const iv = setInterval(() => setIdx(i => (i + 1) % sales.length), 4200);
    return () => clearInterval(iv);
  }, [sales.length]);
  if (!sales.length) return null;
  const s = sales[idx];
  return (
    <div className="ce-proof-pill" data-testid="social-proof-ticker">
      <span className="ce-proof-dot" />
      <span className="ce-proof-text">
        <strong>{s.name}</strong> from {s.city} just sold a <strong>{s.model}</strong> for <strong>${s.price}</strong>
        <span className="ce-proof-time"> · {s.time_ago}</span>
      </span>
      <CEStyles />
    </div>
  );
}


/* ─── 2) Trust Strip below the offer (P0 trust) ────────────────────────── */
export function TrustStrip() {
  return (
    <div className="ce-trust-strip" data-testid="trust-strip">
      <div className="ce-trust-item"><Star className="w-3.5 h-3.5 fill-current" /> <span><strong>5.0★</strong> · 470 reviews</span></div>
      <div className="ce-trust-item"><Award className="w-3.5 h-3.5" /> <span>Hundreds of devices bought</span></div>
      <div className="ce-trust-item"><CheckCircle2 className="w-3.5 h-3.5" /> <span>Insured shipping</span></div>
      <CEStyles />
    </div>
  );
}


/* ─── 3) Price-Lock Badge (P0 trust) ───────────────────────────────────── */
export function PriceLockBadge() {
  return (
    <div className="ce-lock-badge" data-testid="price-lock-badge">
      <Lock className="w-3.5 h-3.5" />
      <span>Locked for <strong>21 days</strong> · price guaranteed</span>
      <CEStyles />
    </div>
  );
}


/* ─── 4) Offer Countdown (P1 urgency) ──────────────────────────────────── */
export function OfferCountdown({ hours = 24 }) {
  const expiry = useMemo(() => Date.now() + hours * 60 * 60 * 1000, [hours]);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const iv = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(iv);
  }, []);
  const ms = Math.max(0, expiry - now);
  const h = String(Math.floor(ms / 3600000)).padStart(2, '0');
  const m = String(Math.floor((ms % 3600000) / 60000)).padStart(2, '0');
  const s = String(Math.floor((ms % 60000) / 1000)).padStart(2, '0');
  return (
    <div className="ce-countdown" data-testid="offer-countdown">
      <Clock className="w-3.5 h-3.5" />
      <span>Bonus expires in <strong>{h}:{m}:{s}</strong></span>
      <CEStyles />
    </div>
  );
}


/* ─── 5) Beat-Any-Quote Badge (P0 trust) ───────────────────────────────── */
export function BeatAnyQuoteBadge({ price = 0 }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button onClick={() => setOpen(true)} className="ce-beat-badge" data-testid="beat-any-quote-badge">
        <TrendingUp className="w-3.5 h-3.5" />
        <span>Show us a <strong>higher quote</strong> →</span>
      </button>
      {open && (
        <div className="ce-modal-backdrop" onClick={() => setOpen(false)}>
          <div className="ce-modal" onClick={e => e.stopPropagation()}>
            <button className="ce-modal-close" onClick={() => setOpen(false)} aria-label="Close" data-testid="beat-any-quote-modal-close"><X className="w-4 h-4" /></button>
            <div className="ce-modal-icon"><TrendingUp className="w-6 h-6" /></div>
            <h3 className="ce-modal-title">Show Us a Higher Quote</h3>
            <p className="ce-modal-text">
              Found a higher offer from Gazelle, Decluttr, eBay, or another buyback service?
              <strong> Show us proof and we'll do our best to beat it by $20.</strong> Here's how it works:
            </p>
            <ul className="ce-modal-list">
              <li>✓ Submit a screenshot of the competitor offer</li>
              <li>✓ Your device must match exactly (same model, storage, condition)</li>
              <li>✓ Offer must be valid at time of comparison</li>
            </ul>
            <a href={`mailto:2aelectronicsservice@gmail.com?subject=Beat%20My%20Quote%20-%20${encodeURIComponent('Current offer $' + price)}`}
               className="ce-modal-cta" data-testid="beat-any-quote-claim">
              Email Us Your Proof →
            </a>
          </div>
        </div>
      )}
      <CEStyles />
    </>
  );
}


/* ─── 6) Seasonal Price-Trend Banner (P1 urgency) ──────────────────────── */
export function PriceTrendBanner({ device }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!device) return;
    let alive = true;
    get(`/api/conversion/price-trend?device=${encodeURIComponent(device)}`)
      .then(d => alive && d?.show && setData(d))
      .catch(() => {});
    return () => { alive = false; };
  }, [device]);
  if (!data) return null;
  const isGood = data.tone === 'good';
  return (
    <div className={`ce-trend ${isGood ? 'ce-trend-good' : 'ce-trend-warn'}`} data-testid="price-trend-banner">
      <Sparkles className="w-3.5 h-3.5" />
      <span>{data.message}</span>
      <CEStyles />
    </div>
  );
}


/* ─── 7) SMS-Offer Button (P0 friction removal) ───────────────────────── */
const formatSmsPhone = (v) => {
  const d = v.replace(/\D/g, '').replace(/^1(?=\d{10})/, '').slice(0, 10);
  if (d.length < 4) return d;
  if (d.length < 7) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
};

export function SmsOfferButton({ deviceQuery, offerPrice, condition }) {
  const [open, setOpen] = useState(false);
  const [phone, setPhone] = useState('');
  const [consent, setConsent] = useState(false);
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const submit = async () => {
    const digits = phone.replace(/\D/g, '');
    if (digits.length < 10 || !consent) return;
    setBusy(true);
    setErrorMsg('');
    try {
      const r = await post('/api/conversion/sms-offer', {
        phone: digits,
        device_query: deviceQuery,
        offer_price: offerPrice,
        condition,
        consent_given: true,
        source_url: typeof window !== 'undefined' ? window.location.href : '',
      });
      if (r?.ok) {
        setSent(true);
        post('/api/conversion/track-event', {
          event: 'sms_offer_sent',
          device_query: deviceQuery,
          offer_price: offerPrice,
        }).catch(() => {});
      } else {
        setErrorMsg(r?.message || 'Could not send SMS, please try again.');
      }
    } catch {
      setErrorMsg('Could not send SMS, please try again.');
    } finally { setBusy(false); }
  };

  const canSend = phone.replace(/\D/g, '').length >= 10 && consent && !busy;

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <>
      <button onClick={() => setOpen(true)} className="ce-sms-btn" data-testid="sms-offer-btn">
        <MessageSquare className="w-4 h-4" />
        <span>Text me this quote</span>
      </button>
      {open && createPortal(
        <div className="ce-modal-backdrop" onClick={() => setOpen(false)} onKeyDown={e => e.key === 'Escape' && setOpen(false)}>
          <div className="ce-modal" role="dialog" aria-modal="true" aria-label="Text me this quote" onClick={e => e.stopPropagation()}>
            <button className="ce-modal-close" onClick={() => setOpen(false)} aria-label="Close" data-testid="sms-offer-modal-close"><X className="w-4 h-4" /></button>
            {sent ? (
              <>
                <div className="ce-modal-icon ce-modal-icon-success"><CheckCircle2 className="w-6 h-6" /></div>
                <h3 className="ce-modal-title">Sent!</h3>
                <p className="ce-modal-text">Check your phone, your <strong>${Math.round(offerPrice)}</strong> offer is locked for 21 days.</p>
                <button onClick={() => setOpen(false)} className="ce-modal-cta" data-testid="sms-offer-close">Got it</button>
              </>
            ) : (
              <>
                <div className="ce-modal-icon"><MessageSquare className="w-6 h-6" /></div>
                <h3 className="ce-modal-title">Text my offer to me</h3>
                <p className="ce-modal-text">We'll text your <strong>${Math.round(offerPrice)}</strong> quote and a 1-click finish link.</p>
                <input
                  type="tel" inputMode="tel" autoComplete="tel" value={phone} onChange={e => setPhone(formatSmsPhone(e.target.value))}
                  placeholder="(555) 123-4567"
                  className="ce-modal-input" data-testid="sms-offer-phone-input"
                />
                <label className="ce-consent-row" data-testid="sms-offer-consent-label">
                  <input
                    type="checkbox"
                    checked={consent}
                    onChange={e => setConsent(e.target.checked)}
                    data-testid="sms-offer-consent-checkbox"
                    className="ce-consent-checkbox"
                  />
                  <span className="ce-consent-text">
                    I agree to receive a one-time SMS from 2A Electronics with my offer and a finish link. Msg &amp; data rates may apply. Reply STOP to opt out, HELP for help.
                  </span>
                </label>
                {errorMsg && <p className="ce-modal-error" data-testid="sms-offer-error">{errorMsg}</p>}
                <button onClick={submit} disabled={!canSend}
                  className="ce-modal-cta" data-testid="sms-offer-send">
                  {busy ? 'Sending…' : 'Send Offer'}
                </button>
                <p className="ce-modal-fine">No spam. We only send the one-time message you requested. <a href="/sms-terms" target="_blank" rel="noreferrer">SMS terms</a>.</p>
              </>
            )}
          </div>
        </div>,
        document.body
      )}
      <CEStyles />
    </>
  );
}


/* ─── 7b) Email-Offer Button, primary CTA while SMS verification pending ─ */
export function EmailOfferButton({ deviceQuery, offerPrice, condition }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [consent, setConsent] = useState(false);
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const submit = async () => {
    if (!emailValid || !consent) return;
    setBusy(true);
    setErrorMsg('');
    try {
      const r = await post('/api/conversion/email-offer', {
        email: email.trim(),
        device_query: deviceQuery,
        offer_price: offerPrice,
        condition,
        consent_given: true,
        source_url: typeof window !== 'undefined' ? window.location.href : '',
      });
      if (r?.ok) {
        setSent(true);
        post('/api/conversion/track-event', {
          event: 'email_offer_sent',
          device_query: deviceQuery,
          offer_price: offerPrice,
        }).catch(() => {});
      } else {
        setErrorMsg(r?.message || 'Could not send email, please try again.');
      }
    } catch {
      setErrorMsg('Could not send email, please try again.');
    } finally { setBusy(false); }
  };

  const canSend = emailValid && consent && !busy;

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <>
      <button onClick={() => setOpen(true)} className="ce-sms-btn" data-testid="email-offer-btn">
        <Mail className="w-4 h-4" />
        <span>Email me this offer</span>
      </button>
      {open && createPortal(
        <div className="ce-modal-backdrop" onClick={() => setOpen(false)}>
          <div className="ce-modal" onClick={e => e.stopPropagation()}>
            <button className="ce-modal-close" onClick={() => setOpen(false)} aria-label="Close" data-testid="email-offer-modal-close"><X className="w-4 h-4" /></button>
            {sent ? (
              <>
                <div className="ce-modal-icon ce-modal-icon-success"><CheckCircle2 className="w-6 h-6" /></div>
                <h3 className="ce-modal-title">Sent!</h3>
                <p className="ce-modal-text">Check your inbox, your <strong>${Math.round(offerPrice)}</strong> offer is locked for 21 days.</p>
                <button onClick={() => setOpen(false)} className="ce-modal-cta" data-testid="email-offer-close">Got it</button>
              </>
            ) : (
              <>
                <div className="ce-modal-icon"><Mail className="w-6 h-6" /></div>
                <h3 className="ce-modal-title">Email my offer to me</h3>
                <p className="ce-modal-text">We'll email your <strong>${Math.round(offerPrice)}</strong> quote and a 1-click finish link.</p>
                <input
                  type="email" inputMode="email" autoComplete="email"
                  value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="ce-modal-input" data-testid="email-offer-input"
                />
                <label className="ce-consent-row" data-testid="email-offer-consent-label">
                  <input
                    type="checkbox"
                    checked={consent}
                    onChange={e => setConsent(e.target.checked)}
                    data-testid="email-offer-consent-checkbox"
                    className="ce-consent-checkbox"
                  />
                  <span className="ce-consent-text">
                    I agree to receive a one-time email from 2A Electronics with my offer and a finish link. Unsubscribe any time by replying.
                  </span>
                </label>
                {errorMsg && <p className="ce-modal-error" data-testid="email-offer-error">{errorMsg}</p>}
                <button onClick={submit} disabled={!canSend}
                  className="ce-modal-cta" data-testid="email-offer-send">
                  {busy ? 'Sending…' : 'Send Offer'}
                </button>
                <p className="ce-modal-fine">No spam. We only send the one-time message you requested.</p>
              </>
            )}
          </div>
        </div>,
        document.body
      )}
      <CEStyles />
    </>
  );
}


/* ─── 8) Exit-Intent Modal (P1 recovery) ──────────────────────────────── */
export function ExitIntentModal({ deviceQuery, offerPrice }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const shown = useRef(false);

  useEffect(() => {
    if (shown.current) return;
    let lastY = 0;
    const onMove = (e) => {
      // Desktop: mouse leaves to top
      if (!shown.current && e.clientY <= 0 && e.relatedTarget == null) {
        shown.current = true; setOpen(true);
        window.removeEventListener('mouseout', onMove);
      }
    };
    const onScroll = () => {
      // Mobile: rapid scroll-up after deep scroll (signal of leaving)
      const y = window.scrollY;
      if (!shown.current && lastY > 800 && lastY - y > 200) {
        shown.current = true; setOpen(true);
        window.removeEventListener('scroll', onScroll);
      }
      lastY = y;
    };
    window.addEventListener('mouseout', onMove);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('mouseout', onMove);
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  const submit = async () => {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return;
    try {
      await post('/api/conversion/email-capture', {
        email, source: 'exit_intent',
        device_query: deviceQuery, last_offer_price: offerPrice,
      });
      // Save abandoned-quote for the recovery email cron
      if (offerPrice && deviceQuery) {
        await post('/api/conversion/abandoned-quote', {
          email, device_query: deviceQuery, offer_price: offerPrice,
        }).catch(() => {});
      }
      post('/api/conversion/track-event', { event: 'exit_intent_captured' }).catch(() => {});
      setSent(true);
    } catch {}
  };

  if (!open) return null;
  return (
    <div className="ce-modal-backdrop" onClick={() => setOpen(false)} data-testid="exit-intent-modal">
      <div className="ce-modal" onClick={e => e.stopPropagation()}>
        <button className="ce-modal-close" onClick={() => setOpen(false)} data-testid="exit-intent-close"><X className="w-4 h-4" /></button>
        {sent ? (
          <>
            <div className="ce-modal-icon ce-modal-icon-success"><Gift className="w-6 h-6" /></div>
            <h3 className="ce-modal-title">$25 Bonus Locked In!</h3>
            <p className="ce-modal-text">Your <strong>+$25</strong> bonus is reserved for <strong>{email}</strong>. We've also emailed your quote so you can come back later.</p>
            <p className="ce-modal-fine">The $25 bonus applies to devices sold for <strong>$250 or more</strong>, added to your final payout after inspection.</p>
            <button onClick={() => setOpen(false)} className="ce-modal-cta">Awesome</button>
          </>
        ) : (
          <>
            <div className="ce-modal-icon ce-modal-icon-bonus"><Gift className="w-6 h-6" /></div>
            <h3 className="ce-modal-title">Wait, add <span className="ce-highlight">+$25</span> to your quote</h3>
            <p className="ce-modal-text">Drop your email and we'll <strong>add $25 to your offer</strong> and email you a copy of your quote so you can come back to it anytime.</p>
            <p className="ce-modal-fine">Bonus applies to devices sold for <strong>$250 or more</strong>, it's added to your final payout after inspection.</p>
            <input
              type="email" inputMode="email" value={email} onChange={e => setEmail(e.target.value)}
              placeholder="you@email.com" className="ce-modal-input" data-testid="exit-intent-email"
            />
            <button onClick={submit} disabled={!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)}
              className="ce-modal-cta" data-testid="exit-intent-submit">
              Claim My $25 Bonus →
            </button>
            <p className="ce-modal-fine">No spam, just your quote + occasional buyback deals. Unsubscribe anytime.</p>
          </>
        )}
      </div>
      <CEStyles />
    </div>
  );
}


/* ─── 9) AI Photo Condition Picker (P0 friction removal) ──────────────── */
export function PhotoConditionPicker({ deviceQuery, onGraded }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [graded, setGraded] = useState(null);

  const handleFile = async (file) => {
    if (!file) return;
    if (file.size > 6_000_000) { alert('Photo too large, please use one under 6 MB.'); return; }
    setBusy(true);
    try {
      const b64 = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(r.result);
        r.onerror = rej;
        r.readAsDataURL(file);
      });
      const result = await post('/api/conversion/grade-condition', {
        image_b64: b64, device_query: deviceQuery,
      });
      setGraded(result);
      onGraded?.(result.condition);
      post('/api/conversion/track-event', {
        event: 'photo_condition_graded',
        device_query: deviceQuery,
        extra: { condition: result.condition, confidence: result.confidence },
      }).catch(() => {});
    } catch (e) {
      alert("Couldn't analyze that photo. Please pick a condition manually below.");
    } finally { setBusy(false); }
  };

  return (
    <>
      <button onClick={() => inputRef.current?.click()} disabled={busy}
        className="ce-photo-btn" data-testid="photo-condition-btn">
        <Camera className="w-4 h-4" />
        <span>{busy ? 'Analyzing photo…' : 'Or snap a photo, AI grades it'}</span>
      </button>
      <input
        ref={inputRef} type="file" accept="image/*" capture="environment"
        onChange={e => handleFile(e.target.files?.[0])}
        style={{ display: 'none' }} data-testid="photo-condition-input"
      />
      {graded && (
        <div className="ce-photo-result" data-testid="photo-graded-result">
          <CheckCircle2 className="w-4 h-4" />
          <span>AI graded condition: <strong className="capitalize">{graded.condition}</strong>
            {graded.confidence > 0 && ` · ${graded.confidence}% sure`}</span>
        </div>
      )}
      <CEStyles />
    </>
  );
}


/* ─── 10) Trade-In Upsell (P2 upsell) ─────────────────────────────────── */
export function TradeUpsell({ cashOffer = 0 }) {
  const storeCredit = Math.round(cashOffer * 1.20);
  if (cashOffer <= 0) return null;
  return (
    <a href="/refurbished" className="ce-upsell" data-testid="trade-upsell">
      <div className="ce-upsell-left">
        <div className="ce-upsell-icon"><ArrowUpRight className="w-5 h-5" /></div>
        <div>
          <p className="ce-upsell-title">Or get <span className="ce-upsell-amount">${storeCredit}</span> store credit</p>
          <p className="ce-upsell-sub">+20% bonus toward a refurbished upgrade</p>
        </div>
      </div>
      <ArrowUpRight className="w-4 h-4 ce-upsell-arrow" />
      <CEStyles />
    </a>
  );
}


/* ─── 11) Referral Kickback Input (P2 upsell) ─────────────────────────── */
export function ReferralKickback({ onApplied }) {
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState('');
  const [applied, setApplied] = useState(false);
  const submit = async () => {
    if (!/^2A-[A-Z0-9]{4,8}$/i.test(code.trim())) return;
    try {
      const r = await post('/api/conversion/referral-redeem', { code: code.trim() });
      if (r?.ok) { setApplied(true); onApplied?.(r.bonus || 25); }
    } catch {}
  };
  if (applied) {
    return (
      <div className="ce-ref-applied" data-testid="referral-applied">
        <CheckCircle2 className="w-3.5 h-3.5" />
        <span>Referral applied, <strong>+$25 bonus</strong> on completion</span>
        <CEStyles />
      </div>
    );
  }
  return (
    <div className="ce-ref-wrap" data-testid="referral-kickback">
      {open ? (
        <div className="ce-ref-row">
          <input type="text" value={code} onChange={e => setCode(e.target.value.toUpperCase())}
            placeholder="2A-XXXXXX" maxLength={11} className="ce-ref-input" data-testid="referral-input" />
          <button onClick={submit} className="ce-ref-apply" data-testid="referral-apply">Apply</button>
        </div>
      ) : (
        <button onClick={() => setOpen(true)} className="ce-ref-toggle" data-testid="referral-toggle">
          <Gift className="w-3 h-3" /> Have a referral code?
        </button>
      )}
      <CEStyles />
    </div>
  );
}


/* ─── Styles (shared sheet) ───────────────────────────────────────────
   We intentionally do NOT short-circuit here: React's reconciler may unmount
   the first inserter while later siblings see a "true" flag and skip insertion,
   leaving the DOM with zero <style> tags. The browser deduplicates identical
   style nodes for us, so multiple copies are harmless. */
function CEStyles() {
  return (
    <style>{`
      /* ── Social proof pill ── */
      .ce-proof-pill {
        display: inline-flex; align-items: center; gap: 8px;
        padding: 6px 12px; border-radius: 999px;
        background: rgba(16,185,129,0.10); border: 1px solid rgba(16,185,129,0.25);
        color: #d1fae5; font-size: 11px; line-height: 1.3;
        animation: ce-fade-up .35s ease-out;
        max-width: 100%;
      }
      html.light .ce-proof-pill {
        background: #ecfdf5 !important; border-color: #a7f3d0 !important; color: #065f46 !important;
      }
      .ce-proof-pill strong { color: #fff; }
      html.light .ce-proof-pill strong { color: #064e3b !important; }
      .ce-proof-dot {
        width: 8px; height: 8px; border-radius: 50%;
        background: #10b981; box-shadow: 0 0 0 0 rgba(16,185,129,0.7);
        animation: ce-pulse 1.5s infinite;
      }
      .ce-proof-time { opacity: .65; font-size: 10px; }

      /* ── Trust strip ── */
      .ce-trust-strip {
        display: flex; flex-wrap: wrap; align-items: center; gap: 6px 14px;
        padding: 8px 12px; border-top: 1px solid rgba(51,65,85,0.5);
        margin-top: 8px;
      }
      html.light .ce-trust-strip { border-color: #e2e8f0 !important; }
      .ce-trust-item {
        display: inline-flex; align-items: center; gap: 4px;
        font-size: 10px; color: #94a3b8; white-space: nowrap;
      }
      html.light .ce-trust-item { color: #475569 !important; }
      .ce-trust-item strong { color: #fff; font-weight: 700; }
      html.light .ce-trust-item strong { color: #0f172a !important; }
      .ce-trust-item svg { color: #06b6d4; }

      /* ── Lock badge ── */
      .ce-lock-badge {
        display: inline-flex; align-items: center; gap: 7px;
        padding: 7px 13px; border-radius: 999px;
        background: linear-gradient(135deg, rgba(6,182,212,0.18), rgba(59,130,246,0.10));
        border: 1px solid rgba(6,182,212,0.45);
        color: #67e8f9; font-size: 11px; font-weight: 600;
        box-shadow: 0 2px 8px -2px rgba(6,182,212,0.25);
        transition: transform .15s, box-shadow .15s;
      }
      .ce-lock-badge:hover { transform: translateY(-1px); box-shadow: 0 4px 14px -2px rgba(6,182,212,0.4); }
      html.light .ce-lock-badge {
        background: linear-gradient(135deg, #ecfeff, #eff6ff) !important;
        border-color: #06b6d4 !important; color: #0e7490 !important;
        box-shadow: 0 1px 3px rgba(6,182,212,0.18) !important;
      }
      .ce-lock-badge strong { color: #ffffff; font-weight: 800; }
      html.light .ce-lock-badge strong { color: #0e7490 !important; }
      .ce-lock-badge svg { color: #22d3ee; }
      html.light .ce-lock-badge svg { color: #0891b2 !important; }

      /* ── Countdown ── */
      .ce-countdown {
        display: inline-flex; align-items: center; gap: 7px;
        padding: 7px 13px; border-radius: 999px;
        background: linear-gradient(135deg, rgba(245,158,11,0.18), rgba(239,68,68,0.10));
        border: 1px solid rgba(245,158,11,0.45);
        color: #fcd34d; font-size: 11px; font-weight: 600;
        box-shadow: 0 2px 8px -2px rgba(245,158,11,0.20);
        position: relative;
      }
      .ce-countdown::before {
        content: ''; width: 6px; height: 6px; border-radius: 50%;
        background: #f59e0b;
        animation: ce-pulse 1.4s infinite;
        flex-shrink: 0;
      }
      html.light .ce-countdown {
        background: linear-gradient(135deg, #fffbeb, #fef3c7) !important;
        border-color: #f59e0b !important; color: #b45309 !important;
        box-shadow: 0 1px 3px rgba(245,158,11,0.18) !important;
      }
      html.light .ce-countdown::before { background: #d97706; }
      .ce-countdown strong { font-variant-numeric: tabular-nums; color: #ffffff; font-weight: 800; }
      html.light .ce-countdown strong { color: #92400e !important; }
      .ce-countdown svg { display: none; } /* replaced by the pulsing dot */

      /* ── Beat-any-quote ── */
      .ce-beat-badge {
        display: inline-flex; align-items: center; gap: 7px;
        padding: 7px 13px; border-radius: 999px;
        background: linear-gradient(135deg, rgba(168,85,247,0.18), rgba(236,72,153,0.10));
        border: 1px solid rgba(168,85,247,0.45);
        color: #d8b4fe; font-size: 11px; font-weight: 600;
        cursor: pointer; transition: all .2s;
        box-shadow: 0 2px 8px -2px rgba(168,85,247,0.25);
      }
      html.light .ce-beat-badge {
        background: linear-gradient(135deg, #faf5ff, #fdf2f8) !important;
        border-color: #a855f7 !important; color: #7e22ce !important;
        box-shadow: 0 1px 3px rgba(168,85,247,0.18) !important;
      }
      .ce-beat-badge:hover { transform: translateY(-1px); box-shadow: 0 4px 14px -2px rgba(168,85,247,0.4); }
      .ce-beat-badge strong { color: #ffffff; font-weight: 800; }
      html.light .ce-beat-badge strong { color: #6b21a8 !important; }
      .ce-beat-badge svg { color: #c084fc; }
      html.light .ce-beat-badge svg { color: #9333ea !important; }

      /* ── Trend banner ── */
      .ce-trend {
        display: flex; align-items: center; gap: 6px;
        padding: 8px 12px; border-radius: 10px;
        font-size: 11px; font-weight: 600;
        margin-top: 6px;
      }
      .ce-trend-warn {
        background: rgba(245,158,11,0.10); border: 1px solid rgba(245,158,11,0.3); color: #fbbf24;
      }
      html.light .ce-trend-warn {
        background: #fffbeb !important; border-color: #fde68a !important; color: #92400e !important;
      }
      .ce-trend-good {
        background: rgba(16,185,129,0.10); border: 1px solid rgba(16,185,129,0.3); color: #34d399;
      }
      html.light .ce-trend-good {
        background: #ecfdf5 !important; border-color: #a7f3d0 !important; color: #065f46 !important;
      }

      /* ── SMS-offer button ── */
      .ce-sms-btn {
        display: inline-flex; align-items: center; gap: 6px;
        padding: 8px 14px; border-radius: 10px;
        background: rgba(6,182,212,0.10); border: 1px solid rgba(6,182,212,0.3);
        color: #22d3ee; font-size: 12px; font-weight: 600;
        transition: all .2s;
      }
      html.light .ce-sms-btn {
        background: #ecfeff !important; border-color: #67e8f9 !important; color: #0e7490 !important;
      }
      .ce-sms-btn:hover { filter: brightness(1.15); transform: translateY(-1px); }

      /* ── Modal backdrop & card ── */
      .ce-modal-backdrop {
        position: fixed; inset: 0; background: rgba(2,6,23,0.7); backdrop-filter: blur(6px);
        display: flex; align-items: center; justify-content: center;
        z-index: 2147483645; padding: 18px;
        animation: ce-fade-in .2s ease-out;
      }
      .ce-modal {
        position: relative;
        max-width: 420px; width: 100%;
        background: #0f172a; border: 1px solid rgba(6,182,212,0.25);
        border-radius: 18px; padding: 24px 22px 22px;
        box-shadow: 0 20px 60px -10px rgba(6,182,212,0.4);
        animation: ce-pop-in .3s cubic-bezier(.34,1.56,.64,1);
        color: #fff;
      }
      html.light .ce-modal { background: #ffffff !important; border-color: #cbd5e1 !important; color: #0f172a !important; box-shadow: 0 20px 60px -15px rgba(15,23,42,0.25) !important; }
      .ce-modal-close {
        position: absolute; top: 12px; right: 12px;
        width: 28px; height: 28px; border-radius: 50%;
        background: rgba(148,163,184,0.15); color: #94a3b8;
        display: flex; align-items: center; justify-content: center;
        transition: all .2s;
      }
      .ce-modal-close:hover { background: rgba(148,163,184,0.25); color: #fff; }
      html.light .ce-modal-close { background: #f1f5f9 !important; color: #475569 !important; }
      .ce-modal-icon {
        width: 48px; height: 48px; border-radius: 14px;
        background: linear-gradient(135deg,#06b6d4,#3b82f6);
        color: #fff; display: flex; align-items: center; justify-content: center;
        margin-bottom: 12px;
        box-shadow: 0 8px 22px -6px rgba(6,182,212,0.45);
      }
      .ce-modal[role="dialog"] > div.ce-modal-icon { min-height: 0 !important; max-width: 48px !important; margin: 0 0 12px !important; border-radius: 14px !important; }
      .ce-modal-icon-success { background: linear-gradient(135deg,#10b981,#059669) !important; box-shadow: 0 8px 22px -6px rgba(16,185,129,0.45) !important; }
      .ce-modal-icon-bonus  { background: linear-gradient(135deg,#f59e0b,#ef4444) !important; box-shadow: 0 8px 22px -6px rgba(245,158,11,0.45) !important; }
      .ce-modal-title { font-size: 19px; font-weight: 800; margin: 0 0 8px; letter-spacing: -0.01em; }
      .ce-modal-text  { font-size: 13.5px; color: #cbd5e1; line-height: 1.55; margin: 0 0 14px; }
      html.light .ce-modal-text { color: #475569 !important; }
      .ce-modal-text strong { color: #fff; }
      html.light .ce-modal-text strong { color: #0f172a !important; }
      .ce-modal-list { font-size: 12.5px; color: #cbd5e1; line-height: 1.7; margin: 0 0 14px; padding-left: 0; list-style: none; }
      html.light .ce-modal-list { color: #475569 !important; }
      .ce-modal-input {
        width: 100%; padding: 12px 14px; border-radius: 11px;
        background: rgba(30,41,59,0.6); border: 1.5px solid rgba(51,65,85,0.8);
        color: #fff; font-size: 15px; outline: none; margin-bottom: 10px;
      }
      html.light .ce-modal-input { background: #ffffff !important; border-color: #cbd5e1 !important; color: #0f172a !important; }
      .ce-modal-input:focus { border-color: #06b6d4; box-shadow: 0 0 0 3px rgba(6,182,212,0.15); }
      .ce-modal-cta {
        display: inline-flex; align-items: center; justify-content: center;
        width: 100%; padding: 13px; border-radius: 11px;
        background: linear-gradient(135deg,#06b6d4,#3b82f6);
        color: #fff; font-weight: 800; font-size: 14.5px;
        text-decoration: none; transition: all .2s;
        box-shadow: 0 8px 22px -6px rgba(6,182,212,0.45);
      }
      .ce-modal-cta:hover:not(:disabled) { filter: brightness(1.1); transform: translateY(-1px); }
      .ce-modal-cta:disabled { opacity: .45; cursor: not-allowed; }
      .ce-modal-fine { font-size: 10.5px; color: #64748b; line-height: 1.5; margin: 10px 0 0; text-align: center; }
      html.light .ce-modal-fine { color: #94a3b8 !important; }
      .ce-modal-fine a { color: #06b6d4; text-decoration: underline; }
      html.light .ce-modal-fine a { color: #0891b2 !important; }
      .ce-modal-error {
        margin: 10px 0 0; padding: 8px 12px; border-radius: 8px;
        background: rgba(239,68,68,0.10); border: 1px solid rgba(239,68,68,0.30);
        color: #fca5a5; font-size: 12px; text-align: center; font-weight: 500;
      }
      html.light .ce-modal-error { background: #fef2f2 !important; border-color: #fecaca !important; color: #b91c1c !important; }

      /* ── TCPA consent checkbox row ── */
      .ce-consent-row {
        display: flex; gap: 10px; align-items: flex-start;
        margin: 12px 0 4px;
        padding: 10px 12px; border-radius: 10px;
        background: rgba(15,23,42,0.55); border: 1px solid rgba(71,85,105,0.45);
        cursor: pointer; user-select: none;
        transition: border-color .15s;
      }
      .ce-consent-row:hover { border-color: rgba(6,182,212,0.55); }
      html.light .ce-consent-row { background: #f8fafc !important; border-color: #cbd5e1 !important; }
      html.light .ce-consent-row:hover { border-color: #06b6d4 !important; }
      .ce-consent-checkbox {
        flex-shrink: 0; margin-top: 2px;
        width: 16px; height: 16px;
        accent-color: #06b6d4; cursor: pointer;
      }
      .ce-consent-text { font-size: 11.5px; color: #cbd5e1; line-height: 1.45; }
      html.light .ce-consent-text { color: #475569 !important; }

      .ce-highlight { color: #22d3ee; }
      html.light .ce-highlight { color: #0891b2 !important; }

      /* ── Photo condition picker ── */
      .ce-photo-btn {
        display: inline-flex; align-items: center; gap: 6px;
        padding: 8px 14px; border-radius: 10px;
        background: rgba(168,85,247,0.10); border: 1px solid rgba(168,85,247,0.3);
        color: #c084fc; font-size: 12px; font-weight: 600;
        transition: all .2s; width: 100%; justify-content: center;
      }
      html.light .ce-photo-btn { background: #faf5ff !important; border-color: #d8b4fe !important; color: #7e22ce !important; }
      .ce-photo-btn:hover:not(:disabled) { filter: brightness(1.15); transform: translateY(-1px); }
      .ce-photo-btn:disabled { opacity: .55; }
      .ce-photo-result {
        display: inline-flex; align-items: center; gap: 6px;
        margin-top: 8px; padding: 6px 12px;
        background: rgba(16,185,129,0.10); border: 1px solid rgba(16,185,129,0.3);
        color: #34d399; font-size: 11.5px; border-radius: 8px; font-weight: 600;
      }
      html.light .ce-photo-result { background: #ecfdf5 !important; border-color: #a7f3d0 !important; color: #065f46 !important; }

      /* ── Trade-in upsell ── */
      .ce-upsell {
        display: flex; align-items: center; justify-content: space-between;
        padding: 12px 14px; border-radius: 12px;
        background: linear-gradient(135deg, rgba(168,85,247,0.10), rgba(59,130,246,0.10));
        border: 1px solid rgba(168,85,247,0.3);
        text-decoration: none; transition: all .2s;
        margin-top: 10px;
      }
      html.light .ce-upsell { background: linear-gradient(135deg, #faf5ff, #eff6ff) !important; border-color: #d8b4fe !important; }
      .ce-upsell:hover { transform: translateY(-1px); filter: brightness(1.1); }
      .ce-upsell-left { display: flex; align-items: center; gap: 10px; }
      .ce-upsell-icon {
        width: 32px; height: 32px; border-radius: 10px;
        background: linear-gradient(135deg,#a855f7,#3b82f6);
        color: #fff; display: flex; align-items: center; justify-content: center;
      }
      .ce-upsell-title { color: #fff; font-size: 13px; font-weight: 700; margin: 0; }
      html.light .ce-upsell-title { color: #0f172a !important; }
      .ce-upsell-amount {
        background: linear-gradient(135deg,#a855f7,#3b82f6);
        -webkit-background-clip: text; -webkit-text-fill-color: transparent;
        font-weight: 800;
      }
      .ce-upsell-sub { color: #94a3b8; font-size: 10.5px; margin: 1px 0 0; }
      html.light .ce-upsell-sub { color: #64748b !important; }
      .ce-upsell-arrow { color: #c084fc; }

      /* ── Referral input ── */
      .ce-ref-wrap { margin-top: 8px; }
      .ce-ref-toggle {
        display: inline-flex; align-items: center; gap: 4px;
        color: #94a3b8; font-size: 10.5px; padding: 2px 8px;
        background: transparent; border: 1px dashed rgba(148,163,184,0.3);
        border-radius: 8px;
      }
      html.light .ce-ref-toggle { color: #64748b !important; border-color: #cbd5e1 !important; }
      .ce-ref-toggle:hover { color: #22d3ee; border-color: rgba(6,182,212,0.5); }
      .ce-ref-row { display: flex; gap: 6px; }
      .ce-ref-input {
        flex: 1; padding: 6px 10px; border-radius: 8px;
        background: rgba(30,41,59,0.6); border: 1px solid rgba(51,65,85,0.8);
        color: #fff; font-size: 12px; outline: none;
        font-family: monospace; letter-spacing: 0.05em; text-transform: uppercase;
      }
      html.light .ce-ref-input { background: #fff !important; border-color: #cbd5e1 !important; color: #0f172a !important; }
      .ce-ref-apply {
        padding: 6px 12px; border-radius: 8px;
        background: linear-gradient(135deg,#06b6d4,#3b82f6);
        color: #fff; font-size: 11px; font-weight: 700;
      }
      .ce-ref-applied {
        display: inline-flex; align-items: center; gap: 6px;
        padding: 5px 10px; border-radius: 999px;
        background: rgba(16,185,129,0.10); border: 1px solid rgba(16,185,129,0.3);
        color: #34d399; font-size: 11px; font-weight: 600; margin-top: 8px;
      }
      html.light .ce-ref-applied { background: #ecfdf5 !important; border-color: #a7f3d0 !important; color: #065f46 !important; }

      /* ── Animations ── */
      @keyframes ce-fade-up {
        from { opacity: 0; transform: translateY(6px); }
        to   { opacity: 1; transform: translateY(0); }
      }
      @keyframes ce-fade-in {
        from { opacity: 0; } to { opacity: 1; }
      }
      @keyframes ce-pop-in {
        from { opacity: 0; transform: scale(0.92); }
        to   { opacity: 1; transform: scale(1); }
      }
      @keyframes ce-pulse {
        0%   { box-shadow: 0 0 0 0 rgba(16,185,129,0.6); }
        70%  { box-shadow: 0 0 0 8px rgba(16,185,129,0); }
        100% { box-shadow: 0 0 0 0 rgba(16,185,129,0); }
      }
    `}</style>
  );
}
